import { useEffect, useState } from "react";
import { FOUNDING_MONTHS, FOUNDING_SEATS, FREE_TURNS, FREE_TURNS_PER_NETWORK, PACK_TURNS_MAX, PACKS, PATRON_PACKS, packFits, PATRON_TURNS_PER_MONTH, PLANS, type PackId, type PatronView } from "../shared/patron";
import { AccountPanel, SIGNED_IN } from "./account";
import { ApiError, api } from "./api";

/**
 * Becoming a Patron: why the sheet opened (the free turns are used, or the month's), the prices first (signing in
 * comes when you choose, so a subscription follows the account, never a cookie), the plans or the packs, and for
 * Patrons their month and a way to manage or cancel. Payment happens on Stripe's own page; afterwards Stripe sends
 * the player back to the page they were on.
 */

export type PatronReason = "patron_needed" | "allocation_used" | "network_free_used" | "thanks" | null;
type Item = "monthly" | "yearly" | PackId;
const usd = (n: number) => `$${n % 1 ? n.toFixed(2) : n}`;
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "long" });
const perDay = Math.round(PATRON_TURNS_PER_MONTH / 30);

export function PatronPanel({ reason, embedded = false }: { reason: PatronReason; embedded?: boolean }) {
  const [v, setV] = useState<PatronView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A plan chosen before signing in: checkout opens as soon as the sign-in is done. */
  const [chosen, setChosen] = useState<Item | null>(null);
  const load = () => api.patron().then((x) => { setV(x); return x; }, () => { setError("Couldn't load your Patron details. Check your connection."); return null; });
  useEffect(() => { void load(); }, []);
  // Signed in elsewhere on the page (Account settings): catch up, and open checkout for a plan chosen meanwhile.
  useEffect(() => {
    const on = () => { void load(); };
    window.addEventListener(SIGNED_IN, on);
    return () => window.removeEventListener(SIGNED_IN, on);
  }, []);
  const go = async (item: Item) => {
    if (v && !v.signedIn) { setChosen(item); return; }
    setBusy(item); setError(null);
    try { location.assign((await api.checkout(item, location.pathname)).url); }
    catch (e) {
      setBusy(null);
      setError(e instanceof ApiError && e.code === "sign_in_needed" ? "Sign in first, then choose." : e instanceof ApiError && e.code === "already_patron" ? "You're already a Patron." : e instanceof ApiError && e.code === "pack_limit" ? `You can hold up to ${PACK_TURNS_MAX.toLocaleString()} pack turns at a time.` : "Checkout couldn't open. Try again in a moment.");
    }
  };
  const manage = async () => {
    setBusy("portal");
    try { location.assign((await api.portal()).url); } catch { setBusy(null); setError("Couldn't open the subscription page. Try again in a moment."); }
  };
  if (!v) return <p className="lm-gate-small">{error ?? "…"}</p>;

  const heading = v.patron?.pass && !reason ? "You're a friend of Weftwake." : reason === "thanks" ? "Thank you. You're a Patron." : reason === "patron_needed" ? `That's your ${FREE_TURNS} free turns.` : reason === "network_free_used" ? "Free turns from this network are used up for today." : reason === "allocation_used" ? "This month's turns are woven." : v.patron ? "You're a Patron." : "Become a Patron";
  const founding = v.foundingLeft > 0;
  const monthly = founding ? PLANS.founding : PLANS.monthly;
  const yearly = PLANS.yearly;
  const saving = Math.round((1 - yearly.usd / (PLANS.monthly.usd * 12)) * 100);
  return (
    <div className="lm-patron">
      <h2 className="lm-sheet-h">{heading}</h2>
      {!v.open && <p>Patron opens soon. During the beta, everything is free.</p>}
      {v.open && !v.patron && (
        <>
          <p>{reason ? "Your story is saved, and nothing in it is lost. " : ""}Patrons keep weaving: {PATRON_TURNS_PER_MONTH.toLocaleString()} turns a month (about {perDay} a day) across every story, and a dream and a plate every night you play.</p>
          {chosen && !v.signedIn ? (
            embedded ? <p className="lm-gate-small">Sign in above first, so your Patronage follows you to every device.</p> : (
              <>
                <p className="lm-gate-small">One step first: sign in, so your Patronage follows you to every device. Then checkout opens.</p>
                <AccountPanel onDone={() => { void load().then((x) => { if (x?.signedIn && chosen) void go(chosen); }); }} />
                <button type="button" className="lm-linkish" onClick={() => setChosen(null)}>Back to the plans</button>
              </>
            )
          ) : (
            <>
              <div className="lm-patron-plans">
                <button type="button" className="lm-patron-plan" disabled={!!busy} onClick={() => go("yearly")}>
                  <strong>{usd(yearly.usd)} a year</strong>
                  <span>About {usd(Math.round((yearly.usd / 12) * 100) / 100)} a month: {saving}% less than paying {usd(PLANS.monthly.usd)} monthly.</span>
                  {!founding && <em className="lm-patron-best">Best value</em>}
                </button>
                <button type="button" className="lm-patron-plan" disabled={!!busy} onClick={() => go("monthly")}>
                  <strong>{usd(monthly.usd)} a month</strong>
                  <span>{founding ? `Founding price for your first ${FOUNDING_MONTHS} months, then ${usd(PLANS.monthly.usd)} a month. Cancel any time.` : "Cancel any time."}</span>
                  {founding && <em className="lm-patron-best">Founding</em>}
                </button>
              </div>
              {founding && <p className="lm-gate-small">The founding price is for the first {FOUNDING_SEATS} Patrons, for their first {FOUNDING_MONTHS} months. {v.foundingLeft} left. We email you before the regular price begins.</p>}
              {reason === "network_free_used" && <p className="lm-gate-small">Free play is {FREE_TURNS} turns per player and up to {FREE_TURNS_PER_NETWORK} a day from one network. They come back tomorrow.</p>}
              {(reason === "patron_needed" || reason === "network_free_used") && (
                <p className="lm-gate-small">
                  Not ready to subscribe?{" "}
                  <button type="button" className="lm-linkish" disabled={!!busy} onClick={() => go("packChapter")}>
                    Finish this chapter: {PACKS.packChapter.turns} turns for {usd(PACKS.packChapter.usd)}
                  </button>
                  , once, no subscription.
                </p>
              )}
            </>
          )}
        </>
      )}
      {v.open && v.patron && (
        <>
          {v.month && <p>This month: {v.month.used.toLocaleString()} of {v.month.limit.toLocaleString()} turns woven; it starts afresh on {day(v.month.resets)}.{v.packTurns ? ` Plus ${v.packTurns} pack turns, kept until you use them.` : ""}</p>}
          {v.patron.pass && <p className="lm-gate-small">A friend's pass: everything a Founding Patron has, on the house. Nothing to pay and nothing renews.</p>}
          {!v.patron.pass && v.patron.renews && <p className="lm-gate-small">{v.patron.cancelsAtEnd ? `Your Patronage ends on ${day(v.patron.renews)}.` : `${PLANS[v.patron.plan].label}; renews on ${day(v.patron.renews)}.`}</p>}
          {(reason === "allocation_used" || (v.month && v.month.used >= v.month.limit * 0.8)) && (
            <>
              <p>A pack keeps the story going until your turns renew; unused pack turns carry over.</p>
              <div className="lm-patron-packs">
                {PATRON_PACKS.map((id) => (
                  <button key={id} type="button" disabled={!!busy || !packFits(id, v.packTurns)} onClick={() => go(id)}><strong>{usd(PACKS[id].usd)}</strong> {PACKS[id].turns.toLocaleString()} turns</button>
                ))}
              </div>
              {PATRON_PACKS.some((id) => !packFits(id, v.packTurns)) && <p className="lm-gate-small">You can hold up to {PACK_TURNS_MAX.toLocaleString()} pack turns at a time.</p>}
            </>
          )}
          {!v.patron.pass && <div className="lm-privacy-actions"><button type="button" disabled={!!busy} onClick={manage}>Manage or cancel</button></div>}
        </>
      )}
      {v.open && !v.patron && v.packTurns > 0 && <p className="lm-gate-small">You have {v.packTurns} pack turns left.</p>}
      {busy && busy !== "portal" && <p className="lm-gate-small">Opening Stripe's secure checkout…</p>}
      {error && <p role="alert">{error}</p>}
      {v.open && <p className="lm-gate-small">Payments are handled by Stripe (sold through Link); Weftwake never sees your card. Plans renew until you cancel, which you can do online at any time. By choosing a plan or pack you agree to the <a href="/terms" target="_blank" rel="noopener">Terms</a>.</p>}
    </div>
  );
}
